import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useDialog } from '@/providers/DialogProvider';
import { deleteOrganizationMutation } from '@/queries/organization.query';
import { useFormik } from 'formik';
import { FunctionComponent } from 'react';
import { analytics } from '@/lib/analytics';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const DeleteOrganizationDialog: FunctionComponent<{ organizationId: string; organizationName: string }> = ({
  organizationId,
  organizationName,
}) => {
  const { t } = useTranslation('orgSettings');
  const { dismissActiveDialog } = useDialog();
  const { refreshOrganizations } = useOrganizations();
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmText: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (values.confirmText !== organizationName) {
        errors.confirmText = t('deleteDialog.nameNoMatch');
      }
      return errors;
    },
    onSubmit: async (_values) => {
      const toastId = toast.loading(t('deleteDialog.deleting'));
      try {
        await execute(deleteOrganizationMutation, { id: organizationId, password: _values.password });
        analytics.capture('Organization Deleted');
        toast.success(t('deleteDialog.deleted'), { id: toastId });
        dismissActiveDialog();
        navigate('/', { replace: true });
        await refreshOrganizations();
      } catch (error) {
        toast.error(t('deleteDialog.failedDelete') + ': ' + (error instanceof Error ? error.message : ''), {
          id: toastId,
        });
      }
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-0.5'>
        <h3 className='font-semibold text-destructive'>{t('deleteDialog.title')}</h3>
        <p className='text-sm leading-none opacity-70'>
          {t('deleteDialog.description', { name: organizationName }).replace(/<\/?bold>/g, '')}
        </p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='password'>{t('deleteDialog.currentPassword')}</Label>
          <Input
            id='password'
            name='password'
            type='password'
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('deleteDialog.currentPasswordPlaceholder')}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='confirmText'>
            {t('deleteDialog.typeToConfirm', { name: organizationName }).replace(/<\/?bold>/g, '')}
          </Label>
          <Input
            id='confirmText'
            name='confirmText'
            value={formik.values.confirmText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={organizationName}
          />
          {formik.touched.confirmText && formik.errors.confirmText && (
            <p className='text-sm text-red-600'>{formik.errors.confirmText}</p>
          )}
        </div>
        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            {t('deleteDialog.cancel')}
          </Button>
          <Button type='submit' variant='destructive' disabled={formik.isSubmitting}>
            {t('deleteDialog.deleteOrganization')}
          </Button>
        </div>
      </form>
    </div>
  );
};
